import { useTranslation } from 'react-i18next';
import type { DishListing, ListingStatus } from '../../types/listing';
import { formatMethodWindows } from '../../utils/timeWindow';
import { formatPriceCents } from '../../utils/currency';

// Per-status badge presentation. Colors map to the Ionic brand palette via the
// `text-brand-*` / `border-brand-*` helpers (src/styles/utils.scss), so they're
// theme-aware for free. `labelKey` resolves under the profile namespace.
const STATUS_META: Record<ListingStatus, { color: string; labelKey: string }> = {
  draft: { color: 'warning', labelKey: 'kitchen.status.draft' },
  active: { color: 'success', labelKey: 'kitchen.status.active' },
  scheduled: { color: 'primary', labelKey: 'kitchen.status.scheduled' },
  sold_out: { color: 'medium', labelKey: 'kitchen.status.soldOut' },
  expired: { color: 'medium', labelKey: 'kitchen.status.expired' },
  canceled: { color: 'danger', labelKey: 'kitchen.status.canceled' },
};

interface ListingHistoryRowProps {
  listing: DishListing;
  onClick: () => void;
}

/**
 * Compact horizontal row for the My Kitchen listing history: thumbnail +
 * title + status badge, over a meta line (price · stock · date) and an order
 * count. Purpose-built for a scannable vertical list — unlike the tall 4:3
 * DishInfoCard used in browse contexts.
 */
export const ListingHistoryRow: React.FC<ListingHistoryRowProps> = ({
  listing,
  onClick,
}) => {
  const { t } = useTranslation(['profile', 'orders']);
  const { title, price, quantity, itemsRemaining, status, thumbnailUrl, imageUrl, createdAt, orderCount } = listing;
  const meta = STATUS_META[status];

  // Each method's window, named, rather than the envelope spanning both.
  const windows = formatMethodWindows(listing);
  const windowLabel = windows
    .map((w) => (windows.length > 1 && w.method ? `${t(`orders:card.fulfillment.${w.method}`)} ${w.label}` : w.label))
    .join(' · ');
  // Prefer the pickup window (what matters for a live/upcoming dish); fall
  // back to the created date for drafts and past listings that have none.
  const dateLabel = windowLabel || t('kitchen.created', { date: new Date(createdAt).toLocaleDateString() });

  const stockLabel =
    quantity != null && itemsRemaining != null
      ? t('kitchen.stockLeft', { remaining: itemsRemaining, total: quantity })
      : null;

  const metaParts = [
    price != null ? formatPriceCents(price, { dropZeroCents: true }) : null,
    stockLabel,
    dateLabel,
  ].filter(Boolean);

  return (
    // `hp-history-row` is the query container; past a rem-based threshold its
    // inner layout reflows from side-by-side to a stacked, full-width text
    // block so nothing truncates at large text sizes. See global.scss.
    <button
      type="button"
      onClick={onClick}
      className="hp-history-row block w-full p-3 text-left hover:bg-surface-100"
    >
      <div className="hp-history-row__inner">
        <div
          className="hp-history-row__thumb shrink-0 overflow-hidden rounded-lg"
          style={{
            // Inline width/height so nothing (Tailwind purge, stale HMR, an
            // overzealous Ionic reset, or a stretch-happy flex parent) can
            // override the thumb size. Premade illustrations have built-in
            // padding around the dish and were clipping at 4rem; 3.25rem
            // leaves room for the plate/border in the source art. Custom
            // photos still get `object-cover` clipping — that's fine.
            width: '3.25rem',
            height: '3.25rem',
            backgroundColor: 'var(--ion-color-light)',
          }}
        >
          {thumbnailUrl || imageUrl ? (
            // object-contain (not cover) so premade placeholder illustrations
            // fit whole inside the square — their built-in padding around the
            // dish was getting cropped under object-cover, which is what read
            // as "clipping". Custom photos will letterbox against the muted
            // background instead of cropping — same trade-off, applied
            // consistently.
            <img
              src={thumbnailUrl ?? imageUrl ?? ''}
              alt={title}
              className="h-full w-full object-contain"
            />
          ) : null}
        </div>

        <div className="hp-history-row__body min-w-0 flex-1">
          <div className="hp-history-row__head">
            <h3 className="hp-history-row__title font-medium">{title}</h3>
            <span
              className={`hp-history-row__badge shrink-0 rounded-full border px-2 py-0.5 text-[11px] font-medium border-brand-${meta.color} text-brand-${meta.color}`}
            >
              {t(meta.labelKey)}
            </span>
          </div>
          <p className="hp-history-row__meta mt-1 text-xs text-muted-foreground">
            {metaParts.join(' · ')}
          </p>
          {orderCount != null && orderCount > 0 && (
            <p className="mt-0.5 text-xs text-brand-primary">
              {t('kitchen.orders', { count: orderCount })}
            </p>
          )}
        </div>
      </div>
    </button>
  );
};
